import { RefObject, useCallback, useRef } from 'react';
import { getFirstFocusableFormField } from '../components/Manager/DatasetForm/utils/getFirstFocusableFormField';
import { focusFormField } from '../utils/forms/focusFormField';

// Focuses the first available form field once the form is initialized, but only
// when no field was already focused (a configured setFocus action takes priority).
export const useAutoFocusFirstFormField = (
  containerRef: RefObject<HTMLElement | null>
): (() => void) => {
  // One-shot per mount — guards against repeated onInitialized fires.
  const focusAppliedRef = useRef(false);

  return useCallback(() => {
    if (focusAppliedRef.current) {
      return;
    }
    focusAppliedRef.current = true;

    // Defer a frame so the setFocus response action (also reacting to readiness)
    // wins when a focusItem is configured.
    window.requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      // setFocus already moved focus into the form — leave it alone.
      if (container.contains(document.activeElement)) {
        return;
      }

      // Same focus engine as the setFocus response action; only the target and
      // trigger differ. shouldSkip bails if setFocus moves focus in before a
      // deferred retry fires, so the first field never steals it.
      focusFormField(
        () => {
          const focusable = getFirstFocusableFormField(container);
          if (!focusable) {
            return null;
          }

          const wrapper =
            focusable.closest<HTMLElement>('[data-form-field]') ?? focusable;
          return { wrapper, focusable };
        },
        {
          shouldSkip: () => container.contains(document.activeElement),
          // Don't let auto-focus pop a control's overlay (e.g. reference quicksearch).
          suppressOpenOnFocus: true,
        }
      );
    });
  }, [containerRef]);
};
